'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Sparkles, Gift, Building2, Sliders, Calendar } from 'lucide-react';

interface PromoFeature {
  id: string;
  stepNumber: string;
  tag: string;
  title: string;
  description: string;
  image: string;
  link: string;
  ctaText: string;
}

const PROMO_FEATURES: PromoFeature[] = [
  {
    id: 'milestones',
    stepNumber: '01',
    tag: 'Keepsake Gifting',
    title: 'Curated Gift Hampers for Every Milestone',
    description:
      'Custom foil-embossed couple initials on rigid keepsake boxes filled with traditional Chettinad Kai Murukku, Tirunelveli Ghee Halwa, and royal savouries for weddings, housewarmings, and special family gatherings.',
    image: '/images/royal_gift_box.jpg',
    link: '#occasions',
    ctaText: 'Explore Hampers',
  },
  {
    id: 'festivals',
    stepNumber: '02',
    tag: 'Seasonal Festivities',
    title: 'Celebrate Auspicious Festivals with Royal Flavors',
    description:
      'Handcrafted festive sweet trays and fresh-batch savoury packages honoring Diwali illumination, Pongal harvests, Onam sadhyas, and sacred celebrations with authentic generational recipes.',
    image: '/images/celebration_treats_festive.jpg',
    link: '#festivals',
    ctaText: 'View Festival Collections',
  },
  {
    id: 'corporate-bulk',
    stepNumber: '03',
    tag: 'Institutional & Wholesale',
    title: 'Corporate Gifting & Bulk Wholesale Orders',
    description:
      'Volume wholesale pricing up to 30% off, metallic company logo lid stamping, and synchronized pan-India delivery engineered for corporate galas, distributor gifting, and grand event banquets.',
    image: '/images/mosaic_brand_pouches.jpg',
    link: '#corporate-bulk',
    ctaText: 'Bulk & Corporate Orders',
  },
  {
    id: 'box-builder',
    stepNumber: '04',
    tag: 'Interactive Customizer',
    title: 'Build Your Own Royal Heritage Snack Box',
    description:
      'Select your keepsake box capacity, choose from 3 to 9 artisan traditional savouries, customize personalized lid branding, and calculate dynamic tiered pricing in real time.',
    image: '/images/custom_box_carton.jpg',
    link: '#occasions',
    ctaText: 'Explore Hampers',
  },
];

export default function DualPromoBanner() {
  return (
    <section className="w-full bg-white select-none border-t border-neutral-100">
      
      {/* 1. Full-Width Clean Headline with Balanced Proportions */}
      <div className="w-full px-4 sm:px-8 py-6 sm:py-8 md:py-9 text-center border-b border-neutral-100">
        <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] uppercase tracking-tight leading-[1.18]">
          THE ART OF CONSCIOUS SNACKING
        </h2>
        <p className="text-[11px] sm:text-xs md:text-sm text-neutral-500 font-sans font-semibold tracking-wider uppercase mt-1.5 sm:mt-2">
          Generational Chettinad Recipes • Pure Cold-Pressed Oils • Zero Preservatives
        </p>
      </div>

      {/* 2. 4-Column Edge-to-Edge Visual Showcase of the 4 Core Offerings */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
        {PROMO_FEATURES.map((feature) => (
          <Link
            key={feature.id}
            href={feature.link}
            className="group relative w-full h-[380px] sm:h-[440px] md:h-[480px] lg:h-[520px] overflow-hidden bg-neutral-900 block focus:outline-none border-r border-b border-white/10 last:border-r-0"
          >
            {/* Background Image with Slow Smooth Hover Zoom */}
            <Image
              src={feature.image}
              alt={feature.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              unoptimized
              className="object-cover w-full h-full transform transition-transform duration-700 ease-out group-hover:scale-108"
            />

            {/* Deep Multi-Layered Vignette Overlay for Crisp Readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/25 p-6 sm:p-7 flex flex-col justify-between transition-opacity duration-300">
              
              {/* Top Tag & Number Badge */}
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-white border border-white/20 font-sans">
                  {feature.tag}
                </span>
                <span className="font-display font-black text-sm text-[#F2BC6F]">
                  {feature.stepNumber}
                </span>
              </div>

              {/* Bottom Info: Title, Description & Action CTA */}
              <div className="space-y-2 sm:space-y-2.5">
                <h3 className="font-display font-semibold text-lg sm:text-xl md:text-2xl text-white leading-tight drop-shadow-md group-hover:text-[#F2BC6F] transition-colors">
                  {feature.title}
                </h3>

                <p className="text-white/80 text-xs leading-relaxed font-sans line-clamp-3">
                  {feature.description}
                </p>

                {/* Interactive CTA Link with sliding arrow */}
                <div className="pt-2 inline-flex items-center space-x-2 text-xs font-bold uppercase tracking-wider text-[#F2BC6F] group-hover:text-white transition-colors">
                  <span>{feature.ctaText}</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1.5 transition-transform" />
                </div>
              </div>

            </div>
          </Link>
        ))}
      </div>

    </section>
  );
}
